import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { SITE, mailto } from "../../data/site";

export interface SiteLayoutProps {
  crumb: string; // 상단 바 왼쪽 경로 표기, 예: "apps / limelink"
  children: React.ReactNode;
  dark?: boolean; // 홈 전용: 상단 바를 남색 히어로 띠 안에 둔다
}

export default function SiteLayout({ crumb, children, dark }: SiteLayoutProps) {
  const { pathname, hash, key } = useLocation();
  const [open, setOpen] = useState(false);

  // 라우터는 스크롤을 옮기지 않는다. 해시가 있으면 그 섹션으로, 없으면 맨 위로.
  // 같은 해시를 다시 눌러도 움직이도록 location.key에 건다. 폭에 따라 숨는 사본이 있어 보이는 쪽을 고른다.
  useEffect(() => {
    const id = decodeURIComponent(hash.slice(1));
    const target = id
      ? Array.from(document.querySelectorAll<HTMLElement>(`[id="${id}"], [data-anchor="${id}"]`)).find(
          (el) => el.getClientRects().length > 0
        )
      : undefined;
    if (!target) {
      window.scrollTo(0, 0);
      return;
    }
    if (target instanceof HTMLDetailsElement) target.open = true;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    target.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
    target.classList.remove("is-target");
    void target.offsetWidth; // 같은 대상을 다시 눌러도 강조 애니메이션을 처음부터
    target.classList.add("is-target");
  }, [pathname, hash, key]);

  // crumb 조각이 실제 경로의 앞부분이면 링크로. 예: "apps / limelink / privacy"
  const parts = crumb ? crumb.split(" / ") : [];
  const crumbs = parts.map((label, i) => {
    const to = "/" + parts.slice(0, i + 1).join("/");
    const linkable = i < parts.length - 1 && (pathname === to || pathname.startsWith(to + "/"));
    return { label, to: linkable ? to : undefined };
  });

  const onApps = pathname === "/apps" || pathname.startsWith("/apps/");

  return (
    <div className={dark ? "site site--dark" : "site"}>
      <header className="site-bar">
        <div className="wrap site-bar-row">
          <span className="site-crumb">
            <Link to="/">{SITE.domain}</Link>
            {crumbs.map((c) => (
              <React.Fragment key={c.label}>
                {" / "}
                {c.to ? <Link to={c.to}>{c.label}</Link> : <span aria-current="page">{c.label}</span>}
              </React.Fragment>
            ))}
          </span>
          <button
            type="button"
            className="site-menu-btn"
            aria-label="메뉴"
            aria-expanded={open}
            aria-controls="site-nav"
            onClick={() => setOpen((o) => !o)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
              {open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
          </button>
          <nav id="site-nav" aria-label="주 메뉴" className={open ? "site-nav is-open" : "site-nav"} onClick={() => setOpen(false)}>
            <ul>
              <li><Link to="/#career">경력</Link></li>
              <li><Link to="/#capability">전문성</Link></li>
              <li><Link to="/principles" aria-current={pathname === "/principles" ? "page" : undefined}>원칙</Link></li>
              <li><Link to="/apps" aria-current={onApps ? "page" : undefined}>앱</Link></li>
              <li><a href={mailto()}>연락하기</a></li>
            </ul>
          </nav>
        </div>
      </header>

      <main className="site-main">{children}</main>

      <footer className="site-footer wrap">
        <div className="site-footer-row">
          <span>© 2026 {SITE.owner}, developer {SITE.developer}</span>
          <span className="site-footer-links">
            <a href={mailto()}>{SITE.email}</a>
            <a href={SITE.resume_pdf} target="_blank" rel="noopener noreferrer">resume.pdf</a>
            <a href={SITE.portfolio_pdf} target="_blank" rel="noopener noreferrer">portfolio.pdf</a>
            <Link to="/legal">/legal</Link>
          </span>
        </div>
      </footer>
    </div>
  );
}
